import React, { useEffect, useState } from "react";
import { Box, ChakraProvider, ColorModeScript, Spinner } from "@chakra-ui/react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { initializeTheme } from "./redux/Theme";
import { setLoggedIn, setLoginData, setRole } from "./redux/auth";
import theme from "./theme/theme";
import { ThemeProvider } from "./context/ThemeContext";
import "./interceptors/axios";
import API from "./context/API";
import axios from "axios";
import AppRoutes from "./routes";
import Layout from "./theme/Layout/Layout";
import { ApiProvider } from "./context/ApiContext";
import LoginPage from "./components/auth/LoginPage";
import OTPInput from "./components/auth/OTPInput";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import ResetPasswordCodePage from "./components/auth/ResetPasswordCodePage";
import { Logout } from "./components/auth/Logout";
import { LoadingProvider } from "./context/LoadingContext";

function App() {
  const dispatch = useDispatch();
  const { isLoggedIn, loginData, role } = useSelector((state) => state.auth);
  const [dynamicRoutes, setDynamicRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(initializeTheme());
  }, [dispatch]);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (window.location.protocol === "https:" && hostname === "kiwikrave.com") {
      window.location.href = "https://merchant.kiwikrave.com";
    }

    const validateAccessToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("access_token");
      const role1 = urlParams.get("role");

      if (token) {
        // Clean up URL immediately to prevent double processing and reload
        // Use replaceState to remove query params without reloading
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        
        localStorage.setItem("access_token", token);

        try {
          let data = null;
          const profileEndpoints = {
            merchant: "merchant/profile",
            admin: "admin/profile",
            merchant_manager: "manager/profile",
            merchant_staff: "staff/profile",
          };
          const endpoint = profileEndpoints[role1];
          if (endpoint) {
            const res = await API.get(endpoint, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            data = res.data.data;
          }

          if (!data) {
            throw new Error("Invalid role or profile not found");
          }

          localStorage.setItem("user", JSON.stringify(data));
          localStorage.setItem("role", data.role);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          
          dispatch(setLoggedIn(true));
          dispatch(setRole(data.role));
          dispatch(setLoginData(data));
        } catch (error) {
          console.error("Token validation failed:", error);
          localStorage.removeItem("access_token");
          localStorage.removeItem("user");
          localStorage.removeItem("role");
          dispatch(setLoggedIn(false));
        }
      }
      // If no token in URL, authSlice already initialized from localStorage
      // Just set loading to false
      setLoading(false);
    };

    validateAccessToken();
  }, [dispatch]);

  if (loading) {
    return (
      <Box textAlign="center" mt="4">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <ThemeProvider>
        <ApiProvider>
          <LoadingProvider>
            <Router>
              <Routes>
                <Route
                  path="/otpverify"
                  element={
                    <OTPInput
                      setIsLoggedIn={(value) => dispatch(setLoggedIn(value))}
                      phone={loginData?.phone}
                      role={role}
                    />
                  }
                />
                <Route
                  path="/login"
                  element={
                    isLoggedIn ? (
                      <Navigate replace to="/dashboard" />
                    ) : (
                      <LoginPage
                        isLoggedIn={isLoggedIn}
                        setIsLoggedIn={(value) => dispatch(setLoggedIn(value))}
                        setRole={(value) => dispatch(setRole(value))}
                        setLoginData={(value) => dispatch(setLoginData(value))}
                      />
                    )
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    isLoggedIn ? (
                      <Navigate replace to="/dashboard" />
                    ) : (
                      <ForgotPasswordPage />
                    )
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    isLoggedIn ? (
                      <Navigate replace to="/dashboard" />
                    ) : (
                      <ResetPasswordPage />
                    )
                  }
                />
                <Route
                  path="/reset-password-code"
                  element={
                    isLoggedIn ? (
                      <Navigate replace to="/dashboard" />
                    ) : (
                      <ResetPasswordCodePage />
                    )
                  }
                />
                <Route
                  path="/"
                  element={
                    <Navigate replace to={isLoggedIn ? "/dashboard" : "/login"} />
                  }
                />
                <Route
                  path="/*"
                  element={
                    isLoggedIn ? (
                      <Layout role={role} setDynamicRoutes={setDynamicRoutes}>
                        <AppRoutes dynamicRoutes={dynamicRoutes} role={role} />
                      </Layout>
                    ) : (
                      <Navigate replace to="/login" />
                    )
                  }
                />
                <Route path="/logout" element={<Logout />} />
              </Routes>
            </Router>
          </LoadingProvider>
        </ApiProvider>
      </ThemeProvider>
    </ChakraProvider>
  );
}

export default App;